import * as React from 'react';
import * as cx from 'classnames';
import * as _ from 'underscore';
import S, { DataSignal } from 's-js';
import { lift } from 's-array';

import SuperCritical, { SuperCriticalComponent, SuperCriticalReference } from 'lib/supercritical';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/TZUtils';
import { tzmoment, tzmomentMod } from 'lib/tzmoment';
import 'lib/kendo-loader';
import { noDefault, noPropagation } from 'lib/eventWrappers';
import UOM from 'lib/UOM';

import { PierGroup } from 'data/models/PierGroup';
import { Berth } from 'data/models/Berth';
import { BerthClosure } from 'data/models/BerthClosure';
import { BerthReservation } from 'data/models/BerthReservation';
import { BollardPosition } from 'data/models/BollardPosition';
import { Issue } from 'data/models/Issue';
import { IssueSeverity } from 'server/enums/Issue';
import { Measurement } from 'data/models/Measurement';
import { BerthReservationStatus } from 'server/enums/BerthReservation';

import { ActionsMenuButton } from 'portcall/actionsmenubutton/view';
import { BerthSelectorView } from 'portcall/berthselector/view';
import { TypeaheadInput } from 'portcall/typeahead/view';
import { DateTimePicker, NullableDateTimePicker } from 'portcall/datetimepicker/view';
import { IssuesShortSummary } from 'portcall/issues/view';
import { NumericInput } from 'portcall/numericinput/view';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { PCModal } from 'portcall/pcmodal/view';

import { 
    ScheduleGrid,
    PortFilterModel,
    CapabilityFilterModel,
    PortModel,
    PierGroupModel,
    ITimelinePosition,
    TimelineItem,
    TimelineItemType,
    BerthReservationModel,
    UnlistedReservationModel,
    BerthClosureModel,
    AddBerthClosureModel,
    ReservationDetailsTool,
    UnlistedReservationDetailsTool,
    BerthClosureDetailsTool,
    EditTimeblockTool,
    NewReservationTool,
    AddShiftTool,
    ChangeBerthTool,
    ReservationOverlapsTool,
    BerthClosureOverlapsTool,
    ITimelineItemTool,
    GridLocation,
    berthBarWidth
} from './model';
import { LightErrorView, ErrorModal } from 'core/validationerror/view';

/*
const DayWidth = 240,
    SCALE = {
        X: DayWidth / (24 * 60 * 60 * 1000), // px per millisecond, expressed as 240 px per day
        Y: {
            [UOM.Foot]: 30 / 100, // px per foot
            [UOM.Meter]: 1 // px per meter
        }
    };
*/

// static values
const COMPACT_BERTH_HEIGHT = 12,
    COMPACT_RES_HEIGHT = 38,
    COMPACT_OVERLAP_GAP = 6,
    MS_PER_DAY = 24 * 60 * 60 * 1000;

let setToolElTimeoutId : number | undefined;

export let ScheduleGridView = SuperCritical(({ model } : { model: ScheduleGrid }) => 
        <div className="schedulegrid">
            <Header model={model} />
            <Grid model={model} />
            <ErrorModal errors={model.Validation} shown={model.ShowErrorModal} title="There are issues with your reservation" />
            <AddBerthClosureModal vm={model} />
        </div>),
    Header = SuperCritical(({model:vm} : { model : ScheduleGrid }) =>
        <div className="carousel-container">
            <div className="pc-actions-links-header header">
                <Filter portFilter={vm.PortFilter} capabilityFilter={vm.CapabilityFilter} />
                <ActionsMenuButton onClick={vm.ToggleActionsMenu} />
                <ActionsMenu model={vm} />
            </div>
        </div>),
    ActionsMenu = SuperCritical(({ model: vm } : { model : ScheduleGrid }) => vm.ShowActionsMenu() ?
        <div className="pc-actions">
            <PCDismissible className="pc-actions-menu pc-card" onDismiss={() => vm.ShowActionsMenu(false)}>
                {vm.CanReserveBerths() &&
                    <div className="action-item">
                        <div>
                            <a href="#!addportcall">Add PortCall</a>
                        </div>
                        <div className="pc-action-item-descr">
                            Create a new PortCall.
                        </div>
                    </div>
                }
                <div className="action-item">
                    <div>
                        <a href="" onClick={noDefault(vm.MenuAction(vm.ToggleCompactView))}>
                            {vm.CompactView() ? 'View Standard Timeline' : 'View Compact Timeline'}
                        </a>
                    </div>
                    <div className="pc-action-item-descr">
                        Change view between the compact and standard timeline.
                    </div>
                </div>
                {/* NOTE: Keep this in sync w/ the checks below. We want the admin separator
                    to be visible iff. the action items below this are visible. */}
                {(vm.CanAddBerthClosures() || vm.CanManageBerths() && vm.User()) &&
                    <div className="pc-admin-separator pc-card-admin-title">
                        Admin <div className="pc-admin-line"></div>
                    </div>
                }
                {vm.CanAddBerthClosures() &&
                    <div className="action-item">
                        <div>
                            <a href="" onClick={noDefault(vm.MenuAction(() => vm.ShowAddBerthClosure(true)))}>
                                Add Closure
                            </a>
                        </div>
                        <div className="pc-action-item-descr">
                            Add a closure to one of your berths.
                        </div>
                    </div>
                }
                {vm.CanManageBerths() && vm.User() &&
                    <div className="action-item">
                        <div>
                            <a href={`#!berthmgrdashboard?id=${vm.User()!.OrganizationId()}`}>View Dashboard</a>
                        </div>
                        <div className="pc-action-item-descr">
                            Manage reservations and services at your berths.
                        </div>
                    </div>
                }
            </PCDismissible>
        </div> : null),
    Filter = SuperCritical(({ portFilter, capabilityFilter } : { portFilter : PortFilterModel, capabilityFilter : CapabilityFilterModel }) =>
        <span className="filters">
            {portFilter.ShowFilter() ? 
                <div className="filter-port">
                    <TypeaheadInput model = {portFilter.Typeahead} className="filter-ctrl port-filter" placeholder="View by island or port" />
                    <a href="" className="show-all" onClick={noDefault(e => portFilter.Query(''))}>Show All</a>
                    <span className="pipe">|</span>
                </div> : null
            }
            <TypeaheadInput model = {capabilityFilter.Typeahead} className="filter-ctrl type-filter" placeholder="View by berth type" />
            <a href="" className="show-all" onClick = {noDefault(e => capabilityFilter.Query(''))}>Show All</a>
        </span>),
    Grid = SuperCritical(({ model } : { model : ScheduleGrid }) => {
        const scale = translateScale(
                model.DateRange.FromDate, 
                model.DateRange.Duration, 
                () => window.innerWidth - berthBarWidth(window.innerWidth), 
                () => new Measurement({ Num: 100, UOM: UOM.Foot }), 
                () => 30,
                model.TZUtils
            ),
            itemEls = ElMap<TimelineItem>(),
            toolEl = S.data<ToolEl | null>(null),
            gridEl = S.value<HTMLDivElement | null>(null);

        // Scroll to the selected timeline item whenever we have a pending request to do so and the element is
        // available.
        S<number | null>(lastReqIdCompleted => {
            const reqId = model.ScrollToToolReqId();

            if (reqId !== null && reqId !== lastReqIdCompleted) {
                const
                    tool = model.CurrentTool(),
                    el = toolEl()?.el ?? (tool?.TimelineItem ? getElMap(itemEls, tool.TimelineItem) : null),
                    grid = gridEl();
                if (el && grid) {
                    const box = el.getBoundingClientRect(),
                        headerHeight = grid.getBoundingClientRect().top + window.scrollY;
                    if (box.top < headerHeight) {
                        window.scrollBy(0, box.top - headerHeight - 50); // 50 = leave a bit of space
                    } else if (box.bottom > window.innerHeight) {
                        window.scrollBy(0, box.bottom - window.innerHeight + 50);
                    }
                    return reqId;
                } else {
                    return lastReqIdCompleted;
                }
            } else {
                return lastReqIdCompleted;
            }
        }, null);

        return <div className="grid">
            <GridHeader model={model} scale={scale} />
            <GridMain model={model} scale={scale} itemEls={itemEls} toolEl={toolEl} gridEl={gridEl} />
        </div>;
    }),
    GridHeader = class extends SuperCriticalComponent<{ model : ScheduleGrid, scale : Scale }> {
        calendarRef : (el : HTMLDivElement | null) => void;
        calendar : any;

        constructor(props : {model : ScheduleGrid, scale : Scale }) {
            super(props);

            this.calendarRef = SuperCriticalReference(this, ({ model } : { model : ScheduleGrid, scale : Scale }, el : HTMLDivElement | null) => {
                this.calendar = (window as any).kendo.jQuery(el).kendoDatePicker({
                    change: function () {
                        var selectedDate = model.TZUtils.tzify(this.value());
                        var diff = selectedDate.getTime() - S.sample(model.DateRange.FromDate).getTime();
                        var diffInDays = Math.round(diff / (24 * 60 * 60 * 1000));
                        model.DateRange.ShiftDate(diffInDays);
                    },
                    open: function () {
                        this.value(model.TZUtils.localify(S.sample(model.DateRange.FromDate)));
                    }
                }).data('kendoDatePicker');
            });
        }

        srender({ model: vm, scale } : { model : ScheduleGrid, scale : Scale }) {
            let dayHeaderWidth = window.innerWidth > 480 ? window.innerWidth - 200 : window.innerWidth - 120;
            return (
                <div className="date-header">
                    <div className="dateselector">
                        <div id="selectorDate" className="date-calendar pointer-cursor" onClick = {noDefault(() => this.calendar.open())}>
                            <span className="glyphicon glyphicon-calendar"></span>
                        </div>
                        <div className="date-arrow" onClick = {noDefault(() => vm.DateRange.ShiftDate(-1))}>
                            <span className="glyphicon glyphicon-chevron-left"></span>
                        </div>
                        <div className="date-text pointer-cursor" onClick = {noDefault(() => vm.DateRange.GoToToday())}>
                            Today
                        </div>
                        <div className="date-arrow" onClick = {noDefault(() => vm.DateRange.ShiftDate(1))}>
                            <span className="glyphicon glyphicon-chevron-left mirror-glyphicon IE-mirror-glyphicon"></span>
                        </div>
                        <div ref = {this.calendarRef} className="datepicker-widget"></div>
                    </div>
                    <svg className="days" height="34" width={dayHeaderWidth}>
                        {vm.DateRange.Days().map(day => {
                            const x = scale.x(day.Day),
                                dayWidth = scale.x(day.NextDay) - x;
                            return (
                                <svg key={+day.Day} className={"day" + (day.Today ? ' today' : '')} y="0" x={x} width={dayWidth}>
                                    <text className="date" y="23" textAnchor="middle" x={dayWidth / 2}>{tzmoment(day.Day).format('MMM D ddd')}</text>
                                </svg> 
                            );
                        })}
                    </svg>
                </div>
            );
        }
    },
    GridMain = SuperCritical(({ model:vm, scale, itemEls, toolEl, gridEl } : {model : ScheduleGrid, scale : Scale, itemEls : ElMap<TimelineItem>, toolEl : DataSignal<ToolEl | null>, gridEl: (el : HTMLDivElement) => void }) =>
        <div className="main">
            <div className="ports" ref={el => el && gridEl(el)}>
                {vm.Ports().filter(port => port.IsDisplayed()).map(port => 
                    <PortView key={port.Port.Id()} port={port} vm={vm} scale={scale} itemEls={itemEls} toolEl={toolEl} />
                )}
            </div>
        </div>
    ),
    PortView = SuperCritical(({ port, vm, scale, itemEls, toolEl } : { port : PortModel, vm : ScheduleGrid, scale : Scale, itemEls : ElMap<TimelineItem>, toolEl : DataSignal<ToolEl | null> }) => {
        return <div key={port.Port.Id()} className="port">
            <PortHeader port={port} scale={scale} />
            <PortPierGroups port={port} vm={vm} scale={scale} itemEls={itemEls} toolEl={toolEl} />
        </div>;
    }),
    PortHeader = SuperCritical(({port, scale} : { port : PortModel, scale : Scale }) =>
        <div className="port-header">
            {port.Port.Name()}
        </div>),
    PortPierGroups = SuperCritical(({ port, vm, scale, itemEls, toolEl } : { port : PortModel, vm : ScheduleGrid, scale : Scale, itemEls : ElMap<TimelineItem>, toolEl : DataSignal<ToolEl | null> }) => {
        const
            // Each piergroup's root element
            pgEls = ElMap<PierGroup>(),
            // Each piergroup's .timeline-items element
            pgItemsEls = ElMap<PierGroup>(),
            bollardEls = ElMap<BollardPosition>(),
            txevent = translateEvent(pgItemsEls, scale),
            tool = port.Tool();

        return (
            <div className={cx("piergroups", {
                'changing-berth': tool instanceof ChangeBerthTool && tool.Seeking(),
                'adding-shift': tool instanceof AddShiftTool && tool.Dragging()
            })}
                onMouseMove={tool instanceof AddShiftTool ? e => tool.Drag(txevent(e)) : undefined }
            >
                {port.PierGroups().filter(pg => pg.IsDisplayed()).map(pg => 
                    <PortPierGroup
                        key={pg.PierGroup.Id()}
                        pg={pg}
                        pgEls={pgEls}
                        pgItemsEls={pgItemsEls}
                        bollardEls={bollardEls}
                        itemEls={itemEls}
                        toolEl={toolEl}
                        vm={vm}
                        txevent={txevent}
                        scale={scale}
                    />
                )}
            </div>
        );
    }),
    PortPierGroup = SuperCritical(({ pg, pgEls, pgItemsEls, bollardEls, itemEls, toolEl, vm, txevent, scale } : {
        pg : PierGroupModel,
        pgEls : ElMap<PierGroup>,
        pgItemsEls : ElMap<PierGroup>,
        bollardEls : ElMap<BollardPosition>,
        itemEls : ElMap<TimelineItem>,
        toolEl : DataSignal<ToolEl | null>,
        vm : ScheduleGrid,
        txevent : TranslateEvent, scale : Scale
    }) => {
        const compact = vm.CompactView(),
            berthHeights = {} as { [ id : number ] : YGeo },
            resOffsets = {} as { [key: string] : number };
        let pgHeight = 0;

        for (let b of pg.Berths()) {
            let height = 0;
            if (compact) {
                // in compact view, our berth is sized visually to the number of overlapping items in it
                // pg.TimelineItems() is sorted in date order, so we scan through it and keep track of the number
                // of overlapping items
                let overlaps = [] as TZDate[],
                    maxOverlaps = 0;
                for (let item of pg.TimelineItems()) {
                    const pos = item.TimelinePosition();
                    if (pos.Berth() === b && pos.Start() && pos.End()) {
                        // remove any items from the list of overlaps which are now past
                        overlaps = overlaps.filter(d => d >= pos.Start()!);
                        // store the overlap of the current item, so we can draw it with the right vertical offset
                        resOffsets[pos.UniqueKey()] = overlaps.length;
                        // add the current berth to record its occupancy of the berth
                        overlaps.push(pos.End()!);
                        // update max depth of overlaps if needed
                        if (overlaps.length > maxOverlaps) maxOverlaps = overlaps.length;
                    }
                }
                height = (maxOverlaps === 0 && b.Length.Num() === 0) ? 0 :
                    COMPACT_BERTH_HEIGHT + (maxOverlaps * COMPACT_RES_HEIGHT) + (maxOverlaps > 1 ? COMPACT_OVERLAP_GAP * (maxOverlaps - 1) : 0);
            } else {
                height = scale.y(b.Length);
            }
            berthHeights[b.Id()] = { y: pgHeight, height };
            pgHeight += height;
        }

        const txPosCoords = translateTimelinePositionCoords(vm.CompactView, scale, berthHeights, resOffsets);

        return (
            <div className="piergroup" ref={el => setElMap(pgEls, pg.PierGroup, el)}>
                <svg className="berths" width={berthBarWidth(window.innerWidth)} height={pgHeight}>
                    {pg.Berths().map(b => 
                        <PortBerth key={b.Id()} berth={b} y={berthHeights[b.Id()].y} height={berthHeights[b.Id()].height} scale={scale} />
                    )}
                    {!compact && <PierGroupBollards pg={pg} scale={scale} bollardEls={bollardEls} />}
                    <BerthFootmarkSelection tool={pg.Tool} txPosCoords={txPosCoords} />
                </svg>
                <PierGroupTimelineItems
                    pg={pg}
                    height={pgHeight}
                    berthHeights={berthHeights}
                    txPosCoords={txPosCoords}
                    pgItemsEl={elMapSlot(pgItemsEls, pg.PierGroup)}
                    itemEls={itemEls}
                    toolEl={toolEl}
                    vm={vm}
                    txevent={txevent}
                    scale={scale}
                />
                <BollardDescriptionTooltip pg={pg} pgEl={elMapSlot(pgEls, pg.PierGroup)} bollardEls={bollardEls} />
            </div>
        );
    }),
    PortBerth = SuperCritical(({berth, y, height, scale} : { berth : Berth, y : number, height : number, scale : Scale }) => {
        let tickDelta = 100,
            tickShift = berth.StartMark.Num() % tickDelta ? (tickDelta - (berth.StartMark.Num() % tickDelta)) : 0, // if our start mark isn't aligned on a tick, we need to shift the first tick accordingly
            tickMarks = _.range(berth.StartMark.Num() + tickShift, berth.StartMark.Num() + berth.Length.Num(), tickDelta),
            ticks = tickMarks.map(m => ({
                Mark: m,
                Major: m % (tickDelta * 5) === 0 && (m - berth.StartMark.Num()) > tickDelta && (berth.StartMark.Num() + berth.Length.Num() - m) > tickDelta,
                Y: scale.y(new Measurement({ Num: m - berth.StartMark.Num(), UOM : berth.StartMark.UOM() }))
            }));
        return (
            <svg className="berth" x="0" y={y} width={berthBarWidth(window.innerWidth)} height={height}>
                <rect className="berthbox" x="0" y="0" width={berthBarWidth(window.innerWidth)} height="100%"></rect>
                <text className="berthlabel" x="10" y={height <= COMPACT_BERTH_HEIGHT ? 11 : 20}>
                    {/* TH 01/13/2017 -- Using xlinkHref instead of plain href to fix bug w/ this link not working in Safari.
                        Problem is, our current version of React typings doesn't recognize xlinkHref.
                        So I'm using the React.createElement API as a work around.
                        TH 01/16/2017 -- UPDATE: The React typings DO recognize xlinkHref, just not for <a> elements.
                        Also, xlink:href is apparently deprecated as of SVG2: https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/xlink:href */}
                    {React.createElement('a', {
                        className: 'name',
                        xlinkHref: '#!berthprofile?id=' + berth.Id()
                    }, [berth.Location() ? berth.Location()!.Name() : ''])}
                    {/* The above React.createElement call is equivalent to the following <a> tag */}
                    {/*<a className="name" xlinkHref={'#!berthprofile?id=' + berth.Id()}>{berth.Location() ? berth.Location()!.Name() : ''}</a>*/}
                    <tspan className="length">{' ' + berth.Length.toString({ places: 0 })}</tspan>
                </text>
                {height <= COMPACT_BERTH_HEIGHT ? null :
                    <g>
                        <text className="startmark" x={berthBarWidth(window.innerWidth) - 10} y="10" textAnchor="end">{berth.StartMark.toString({ units: false, places: 0 })}</text>
                        <text className="endmark" x={berthBarWidth(window.innerWidth) - 10} textAnchor="end" y={height - 5}>{berth.StartMark.add(berth.Length).toString({ units: false, places: 0 })}</text>
                    </g>
                }
                {height <= COMPACT_BERTH_HEIGHT + COMPACT_RES_HEIGHT ? null : ticks.map(tick => 
                    <g key={tick.Mark}>
                        <line className="tick" x1={berthBarWidth(window.innerWidth) - 5} x2={berthBarWidth(window.innerWidth)} y1={tick.Y} y2={tick.Y}></line>
                        {tick.Major ? <text className="ticklabel" x="190" textAnchor="end" y={tick.Y + 4}>{tick.Mark}</text> : null}
                    </g>
                )}
            </svg>
        );

    }),
    PierGroupBollards = SuperCritical(({ pg, scale, bollardEls } : {
        pg : PierGroupModel,
        scale : Scale,
        bollardEls : ElMap<BollardPosition>
    }) =>
        // We lift bollards out to their own <g> element outside of each berth's svg.berth element so that
        // they're visible even if the bollard's designated Footmark actually matches a DIFFERENT berth in
        // the piergroup.
        <g>
            {pg.Bollards().map(bol =>
                <circle
                    ref={el => setElMap(bollardEls, bol, el)}
                    key={bol.Id()}
                    className={cx('bollard', bol.CssClass())}
                    cx={berthBarWidth(window.innerWidth) - 12}
                    cy={scale.y(bol.PierMark)}
                    r={4}
                    // We create a tooltip-like effect via JS so we can piggyback on the same popup positioning
                    // logic that we use for the various ITimelineItemTool impls.
                    onMouseEnter={() => pg.SelectBollard  (bol)}
                    onMouseLeave={() => pg.DeselectBollard(bol)}
                />
            )}
        </g>
    ),
    BerthFootmarkSelection = SuperCritical(({ tool: _tool, txPosCoords } : { tool : () => ITimelineItemTool | null, txPosCoords : (pos : ITimelinePosition) => Rectangle }) => {
        var tool = _tool(), 
            sel = tool && tool.TimelineItem ? tool.TimelineItem.FootmarkSelection() : null,
            fm = sel && sel.Footmark,
            pos = sel && sel.Position,
            length = pos && pos.Length(),
            coords = pos && txPosCoords(pos);

        return !tool || !coords || !fm || !length ? null :
            <g>
                <line className="selectedtick" x1={berthBarWidth(window.innerWidth) - 20} x2={berthBarWidth(window.innerWidth)} y1={coords.y} y2={coords.y}></line>
                <text className="selectedticklabel" x={berthBarWidth(window.innerWidth) - 25} textAnchor="end" y={coords.y + 4}>{fm.toString({ units: false, places: 0 })}</text>
                <line className="selectedtick" x1={berthBarWidth(window.innerWidth) - 20} x2={berthBarWidth(window.innerWidth)} y1={coords.y + coords.height} y2={coords.y + coords.height}></line>
                <text className="selectedticklabel" x={berthBarWidth(window.innerWidth) - 25} textAnchor="end" y={coords.y + coords.height + 4}>{fm.add(length).toString({ units: false, places: 0 })}</text>
            </g>;
    }),
    PierGroupTimelineItems = SuperCritical(({ pg, height, berthHeights, txPosCoords, pgItemsEl, itemEls, toolEl, vm, txevent, scale } : { 
            pg : PierGroupModel, 
            height : number, 
            berthHeights : { [ id : number ] : YGeo }, 
            txPosCoords : (pos : ITimelinePosition) => Rectangle, 
            pgItemsEl : DataSignal<Element | null>,
            itemEls : ElMap<TimelineItem>,
            toolEl : DataSignal<ToolEl | null>,
            vm : ScheduleGrid, 
            txevent : TranslateEvent,
            scale : Scale
        }) => {
            const
                width = scale.x(vm.DateRange.FromDate(), vm.DateRange.ToDate()),
                nowX = scale.x(vm.DateRange.Now);
            return (
                <div
                    className="timeline-items"
                    style={{ height: height + 'px' }}
                    ref={el => el && pgItemsEl(el)}
                >
                    <svg width={width} height={height} onClick={noDefault((e : React.MouseEvent<SVGElement>) => vm.OpenSpaceClick(txevent(e)!))}>
                        {vm.DateRange.Days().map(day => {
                            let x = scale.x(day.Day),
                                dayWidth = scale.x(day.NextDay) - x;
                            return <g key={+day.Day}>
                                {day.IsLoaded() ? null : 
                                    <g>
                                        <foreignObject y="10" height="24" width="24" x={x + dayWidth / 2 - 12}>
                                            <div className="day-loading-indicator"></div>
                                        </foreignObject>
                                    </g>
                                }
                                <line className="day-divider" y1="0" y2="100%" x1={x + dayWidth} x2={x + dayWidth}></line>
                            </g>;
                        })}
                        <line className="now" y1="0" y2="100%" x1={nowX} x2={nowX}></line>
                        {pg.Berths().map(b => {
                            let geo = berthHeights[b.Id()];
                            return (
                                <g key={b.Id()}>
                                    <line className="berth-divider" x1="0" x2="100%" y1={geo.y} y2={geo.y}></line>
                                    <line className="berth-divider" x1="0" x2="100%" y1={geo.y + geo.height} y2={geo.y + geo.height}></line>
                                </g>
                            );
                        })}
                        <g>
                            {pg.TimelineItems().map(item => {
                                const pos = item.TimelinePosition(),
                                    coords = txPosCoords(pos),
                                    key = pos.UniqueKey();
                                return (
                                    item.Type === TimelineItemType.BerthReservation ?
                                        <Reservation key={key} res={item} vm={vm} scale={scale} txevent={txevent} coords={coords} itemEls={itemEls} /> :
                                    item.Type === TimelineItemType.UnlistedReservation ?
                                        <UnlistedReservation key={key} unlistedReservation={item} vm={vm} scale={scale} coords={coords} itemEls={itemEls} /> :
                                    item.Type === TimelineItemType.BerthClosure ?
                                        <BerthClosureView key={key} closure={item} vm={vm} scale={scale} coords={coords} itemEls={itemEls} /> :
                                    null
                                );
                            })}
                            <PierGroupNewReservation pg={pg} scale={scale} txevent={txevent} toolEl={toolEl} />
                            <PierGroupAddShiftIndicator pg={pg} scale={scale} toolEl={toolEl} />
                        </g>
                    </svg>
                    <PierGroupToolPopup pg={pg} itemEls={itemEls} pgItemsEl={pgItemsEl} toolEl={toolEl} model={vm} />
                </div>
            );
        }),
    UnlistedReservation = SuperCritical(({ unlistedReservation: model, vm, scale, coords, itemEls } : {
        unlistedReservation : UnlistedReservationModel,
        vm : ScheduleGrid,
        scale : Scale,
        coords : Rectangle,
        itemEls : ElMap<TimelineItem>
    }) => {
        const
            ur = model.UnlistedReservation,
            pg = ur.Berth() && ur.Berth()!.PierGroup(),
            { x, y, width, height } = coords,
            pgHeight = pg ? scale.y(pg.Length) : 0,
            visibleHeight = Math.min(height, pgHeight - y),
            clippath = 'uresclippath-' + ur.Id(),
            schedAuth = ur.Berth() && ur.Berth()!.SchedulingAuthorityId();

        return (
            <g
                className={cx("unlisted-reservation-container", { 
                    // DC 09/14/2017 -- Suppress Unlisted / Unavailable information when no Scheduling Authority present
                    'unlisted-reservation-and-hidden' : !schedAuth
                })}
            >
                <svg
                    className="unlisted-reservation"
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    // NOTE: We don't apply "overflow: visible" to this particular element, so the Firefox
                    // "overflow: visible" bug noted for the Reservation component below doesn't apply.
                    ref={el => setElMap(itemEls, model, el)}
                >
                    <defs>
                        <clipPath id={clippath}>
                            <rect x="0" y="1" rx="2" ry="2" width={width} height={height}></rect>
                        </clipPath>
                    </defs>
                    <rect className="resbox" x="0" y="1" rx="2" ry="2" width={width} height={height}></rect>
                    <text className="name" x={Math.max(10, -x)} y={visibleHeight / 2} clipPath={'url(#' + clippath + ')'}>Berth Unavailable</text>
                    <text className="length" x={Math.max(10, -x)} y={(visibleHeight / 2) + 15} clipPath={'url(#' + clippath + ')'}>{ur.Length.toString({ places: 0 })}</text>
                    <rect className="selectbox" x="0" y="0"
                        onClick={noPropagation(() => vm.TimelineItemClick(model))}
                        width={width}
                        height={height}
                    ></rect>
                </svg>
            </g>
        );
    }),
    BerthClosureView = SuperCritical(({ closure: model, vm, scale, coords, itemEls } : {
        closure : BerthClosureModel,
        vm : ScheduleGrid,
        scale : Scale,
        coords : Rectangle,
        itemEls : ElMap<TimelineItem>
    }) => {
        const
            bcl = model.BerthClosure,
            pg = bcl.Berth() && bcl.Berth()!.PierGroup(),
            { x, y, width, height } = coords,
            pgHeight = pg ? scale.y(pg.Length) : 0,
            visibleHeight = Math.min(height, pgHeight - y),
            clippath = 'bclclippath-' + bcl.Id(),
            // status flags
            // Consider both this closure's issues and issues any reservations might have
            // b/c of this closure as part of this closure's issue count.
            issueCount = bcl.Issues().length + bcl.ReservationIssues().length,
            invalid = !model.ValidForSubmit();

        return (
            <g className="berth-closure-container">
                <svg
                    className={cx("berth-closure", { invalid })}
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                >
                    <defs>
                        <clipPath id={clippath}>
                            <rect x="0" y="1" rx="2" ry="2" width={width} height={height}></rect>
                        </clipPath>
                    </defs>
                    <rect
                        className="resbox"
                        x="0"
                        y="1"
                        rx="2"
                        ry="2"
                        width={width}
                        height={height}
                        // We apply "overflow: visible" to the parent <svg> element for berth closures, so
                        // need the same Firefox workaround as for the Reservation component below.
                        ref={el => setElMap(itemEls, model, el)}
                    />

                    {issueCount > 0 &&
                        <svg className="badge issue-badge" x={width - 30} y="0" width="30" height="30">
                            <circle cx="14" cy="16" r="10" />
                            <text x="14" y="16" textAnchor="middle" ref={el => el && (el as any).setAttribute('dominant-baseline', "central")}>{issueCount}</text>
                        </svg>
                    }

                    <text className="name" x={Math.max(10, -x)} y={visibleHeight / 2} clipPath={'url(#' + clippath + ')'}>Closure &mdash; {bcl.Title()}</text>
                    <rect className="selectbox" x="0" y="0"
                        onClick={noPropagation(() => vm.TimelineItemClick(model))}
                        width={width}
                        height={height}
                    ></rect>
                </svg>
            </g>
        );
    }),
    Reservation = SuperCritical(({ res:model, vm, scale, txevent, coords, itemEls } : { 
        res : BerthReservationModel,
        vm : ScheduleGrid,
        scale : Scale,
        txevent : TranslateEvent,
        coords : Rectangle,
        itemEls : ElMap<TimelineItem>
    }) => {
        const
            res = model.Reservation,
            pt = model.ItineraryPoint,
            berth = res.Berth(),
            pg = berth && berth.PierGroup(),
            pc = pt.PortCall(),
            tool = model.Tool(),
            // position
            compact = vm.CompactView(),
            { x, y, width, height } = coords,
            pgHeight = pg ? scale.y(pg.Length) : 0,
            visibleHeight  = Math.min(height, pgHeight - y),
            bufferX = scale.x(pt.Arrival.Effective(), tzmomentMod(vm.TZUtils, pt.Arrival.Effective(), d => d.add(berth ? berth.TimeBuffer() : 0, 'minutes'))),
            bufferY = berth ? scale.y(berth.LengthBuffer) : 0,
            showBufferShadow =
                tool instanceof EditTimeblockTool
             || tool instanceof ChangeBerthTool
             // TODO: I'm not seeing a visible shadow for the AddShiftTool. I haven't dug into why.
             // Is a shadow still desirable for the AddShiftTool, or can we maybe cut this line?
             || tool instanceof AddShiftTool && tool.Dragging(),
            clippath = 'resclippath-' + res.Id(),
            // status flags
            pending          = res.Status() === BerthReservationStatus.Requested || res.Status() === BerthReservationStatus.Conflicted,
            conflicted       = res.Status() === BerthReservationStatus.Conflicted,
            issueCount       = res.Issues().length,
            future           = res.Status() == BerthReservationStatus.Booked && pt.Arrival.Actual() === null,
            inport           = pt.Arrival.Actual() !== null && pt.Departure.Actual() === null,
            departed         = pt.Arrival.Actual() !== null && pt.Departure.Actual() !== null,
            editingTimeblock = tool instanceof EditTimeblockTool,
            canEditArrival   = pt.Arrival.Actual() === null ? model.Edit() : model.EditActuals(),
            canEditDeparture = pt.Departure.Actual() === null ? model.Edit() : model.EditActuals(),
            canDragTimeblock = canEditArrival && canEditDeparture,
            invalid          = _.keys(model.Validation()).length !== 0;
        return (
            <g className="berth-reservation-container">
                <svg
                    className={cx("reservation", { pending, conflicted, future, inport, departed, 'editing-timeblock': editingTimeblock, 'can-drag-timeblock': canDragTimeblock, invalid })}
                    x      ={x}
                    y      ={y}
                    width  ={width}
                    height ={height}
                >
                    <defs>
                        <clipPath id={clippath}>
                            <rect x="0" y="1" rx="2" ry="2" width={width} height={height}></rect>
                        </clipPath>
                    </defs>
                    
                    {showBufferShadow &&
                        <rect
                            className={cx('buffer-shadow', {
                                'attention-grabber': tool instanceof ChangeBerthTool && tool.Seeking() && tool.AttentionGrabber.Active()
                            })}
                            rx     ="2"
                            ry     ="2"
                            x      ={-bufferX}
                            y      ={compact ? 0 : -bufferY}
                            width  ={width + (2 * bufferX)}
                            height ={height + (compact ? 0 : 2 * bufferY)}
                            // If we have a buffer shadow, then make IT the popup target to avoid covering up
                            // the shadow w/ the popup.
                            ref={el => setElMap(itemEls, model, el)}
                        />
                    }

                    <rect
                        className="resbox"
                        x="0"
                        y="1"
                        rx="2"
                        ry="2"
                        width={width}
                        height={height}
                        // Use this <rect> element as the target for any <Popup />'s related to this
                        // reservation. On most browsers, it would work just fine to target the parent <svg>
                        // element instead, but on Firefox that interferes w/ popup positioning b/c we give
                        // that element "overflow: visible" via CSS, and Firefox apparently has a bug
                        // calculating the bounding client rect for <svg> elements that:
                        // 1. Are nested within some other SVG content, AND
                        // 2. Have x > 0 or y > 0, AND
                        // 3. Have "overflow: visible", AND
                        // 4. Have some displayed content. As in, not empty, not just a <rect> w/ height and width
                        //    === 0, not just a <rect> w/ "display: none" applied, etc.
                        // If the conditions above are met, then calling getBoundingClientRect() on the relevant
                        // <svg> element returns a rect whose width and/or height are larger than they should
                        // be, apparently b/c they incorporate the width/height above and/or to the left of the
                        // relevant <svg> element (and sometimes some of the width/height below and/or to the
                        // right of that element too). You can see the same thing if you inspect the relevant
                        // <svg> element in Firefox's dev tools; pay attention to the screen area that Firefox
                        // highlights when you hover over the element.
                        // I've filed a bug report w/ Firefox about this. See
                        // https://bugzilla.mozilla.org/show_bug.cgi?id=1709251
                        // Fortunately, targeting this <rect> element works just fine on Firefox and other
                        // browsers too. The only wrinkle is we'll switch up our target if we're rendering a
                        // buffer shadow for this reservation; see a few lines above.
                        ref={showBufferShadow ? undefined : el => setElMap(itemEls, model, el)}
                    />

                    {res.Rafting() ?
                        <svg className="badge rafting-badge" x={width - 56} y="0" width="30" height="30">
                            <circle cx="14" cy="16" r="10" />
                            <text x="14" y="16" textAnchor="middle" ref={el => el && (el as any).setAttribute('dominant-baseline', "central")}>R</text>
                        </svg>
                    : null}

                    {issueCount > 0 ?
                        <svg className="badge issue-badge" x={width - 30} y="0" width="30" height="30">
                            <circle cx="14" cy="16" r="10" />
                            <text x="14" y="16" textAnchor="middle" ref={el => el && (el as any).setAttribute('dominant-baseline', "central")}>{issueCount}</text>
                        </svg>
                    : null}

                    <text className="name" x={Math.max(10, -x)} y={visibleHeight / 2} clipPath={'url(#' + clippath + ')'}>{pc?.DisplayVesselIdentifier()}</text>
                    <text className="length" x={Math.max(10, -x)} y={(visibleHeight / 2) + 15} clipPath={'url(#' + clippath + ')'}>{res.Length.toString({ places: 0 })}</text>
                    <rect className="selectbox" x="0" y="0" 
                        onMouseDown = {tool instanceof EditTimeblockTool && canDragTimeblock
                                ? noDefault((e : React.MouseEvent<SVGElement>) => translateDrag(txevent, (tool as EditTimeblockTool).StartDragReservation(txevent(e)!, compact)))
                                : undefined}
                        onClick={noPropagation(() => vm.TimelineItemClick(model))}
                        width={width - 0}
                        height={height - 0}
                    ></rect>
                    {tool instanceof EditTimeblockTool ?
                        <g>
                            {!canEditArrival ? null : [
                                <circle className="resize-handle" cx="0" cy="50%" r="3"></circle>,
                                <rect className="resize-zone" x="-5" y="0" height="100%" width="10" 
                                    onMouseDown={noDefault(() => translateDrag(txevent, (tool as EditTimeblockTool).DragDate(true)))}></rect> 
                            ]}
                            {!canEditDeparture ? null : [
                                <circle className="resize-handle" cy="50%" r="3" cx={width}></circle>,
                                <rect className="resize-zone" y="0" height="100%" width="10" x={width - 5} 
                                    onMouseDown={noDefault(() => translateDrag(txevent, (tool as EditTimeblockTool).DragDate(false)))}></rect>
                            ]}
                        </g>
                    : null}
                </svg>

                {y < 0 ? <text className="overhang" textAnchor="middle" x={x + width / 2} y={8}>overhang</text> : null}
                {y + height > pgHeight ? <text className="overhang" textAnchor="middle" x={x + width / 2} y={pgHeight - 8}>overhang</text> : null}
            </g>
        );
    }),
    PierGroupNewReservation = SuperCritical(({pg, scale, txevent, toolEl} : { pg : PierGroupModel, scale : Scale, txevent : TranslateEvent, toolEl : DataSignal<ToolEl | null> }) => {
        const tool = pg.Tool();

        if (!(tool instanceof NewReservationTool)) return null;

        const x = scale.x(tool.Arrival()),
            y = scale.y(tool.PierMark),
            width = scale.x(tool.Arrival(), tool.Departure()),
            height = scale.y(tool.Length);

        return <svg className="new-reservation" x={x - 5} y={y} height={height} width={width + 10} ref={el => setToolEl(toolEl, tool, el)} onClick={noPropagation(() => {})}>
            <rect className="outline" x="5" y="0" height="100%" width={width}></rect>
            <foreignObject x="5" y="0" width={width} height={height}>
                <div style={{ paddingLeft: '5px', paddingRight: '5px', height: height+'px', width: width+'px' }}>
                    <span className="length-available">
                        Max Length Available: <br />
                        {tool.Length.toString({ places: 0 })}
                    </span>
                </div>
            </foreignObject>
            <circle className="resize-handle" cx="5" cy="50%" r="3"></circle>
            <circle className="resize-handle" cy="50%" r="3" cx={width + 5}></circle>
            <rect className="resize-zone" x="0" y="0" height="100%" width="10" 
                onMouseDown = {noDefault(() => translateDrag(txevent, tool.StartDateDrag(true)))}
            ></rect>
            <rect className="resize-zone" y="0" height="100%" width="10" x={width} 
                onMouseDown = {noDefault(() => translateDrag(txevent, tool.StartDateDrag(false)))}
            ></rect>
        </svg>;
    }),
    PierGroupAddShiftIndicator = SuperCritical(({ pg, scale, toolEl } : { pg : PierGroupModel, scale : Scale, toolEl : DataSignal<ToolEl | null> }) => {
        const tool = pg.Tool();

        if (!(tool instanceof AddShiftTool)) return null;
        
        const x = scale.x(tool.Arrival()),
            y = scale.y(tool.PierMark),
            height = scale.y(tool.NewReservation.Length),
            width = scale.x(tool.Arrival(), tool.Departure());

        return (
            <svg className={"new-shift-location" + (tool.Dragging() && tool.AttentionGrabber.Active() ? " attention-grabber" : "")}
                x       = {x}
                y       = {y}
                height  = {height}
                width   = {width}
                onClick = {noDefault(() => tool.OpenSpaceClick())}
                ref     = {el => setToolEl(toolEl, tool, el)}
            >
                <rect className="outline" x="0" y="0" height="100%" width="100%"></rect>
                {tool.Dragging() ? <text className="click-label" textAnchor="end" x={width - 20} y={height / 2 + 4}>CLICK TO ADD SHIFT</text> : null}
            </svg>
        );
    }),
    PierGroupToolPopup = SuperCritical(({ pg, pgItemsEl, itemEls, toolEl : _toolEl, model: m } : {
        pg: PierGroupModel,
        pgItemsEl : () => Element | null,
        itemEls : ElMap<TimelineItem>,
        toolEl : DataSignal<ToolEl | null>,
        model: ScheduleGrid
    }) => {
        const
            tool = pg.Tool(),
            itemEl = tool?.TimelineItem && elMapSlot(itemEls, tool.TimelineItem),
            toolEl = () => _toolEl()?.el ?? null;

        return (
            tool instanceof NewReservationTool                  ? <NewReservationPopup tool={tool} model={m} target={toolEl} container={pgItemsEl} /> :
            tool instanceof AddShiftTool && !tool.Dragging()    ? <ConfirmAddShiftPopup tool={tool} model={m} target={toolEl} container={pgItemsEl} /> :
            tool instanceof ReservationDetailsTool              ? <ReservationDetailsPopup tool={tool} target={itemEl!} container={pgItemsEl} /> :
            tool instanceof UnlistedReservationDetailsTool      ? <UnlistedReservationDetailsPopup tool={tool} target={itemEl!} container={pgItemsEl} /> :
            tool instanceof BerthClosureDetailsTool             ? <BerthClosureDetailsPopup tool={tool} vm={m} target={itemEl!} container={pgItemsEl} /> :
            tool instanceof EditTimeblockTool                   ? <EditTimeBlockPopup tool={tool} model={m} target={itemEl!} container={pgItemsEl} /> :
            tool instanceof ChangeBerthTool && !tool.Seeking()  ? <ChangeBerthPopup tool={tool} model={m} target={itemEl!} container={pgItemsEl} /> :
            tool instanceof ReservationOverlapsTool             ? <ReservationOverlapsPopup tool={tool} target={itemEl!} container={pgItemsEl} /> :
            tool instanceof BerthClosureOverlapsTool            ? <BerthClosureOverlapsPopup tool={tool} target={itemEl!} container={pgItemsEl} /> :
            null
        );
    }),
    Popup = class extends SuperCriticalComponent<{ className? : string, target : () => Element | null, container : () => Element | null }> {
        ref : (el : HTMLDivElement | null) => void;

        constructor(props : { className? : string, target : () => Element | null, container : () => Element | null }) {
            super(props);

            this.ref = SuperCriticalReference(this, ({ className, target:_target, container:_container } : { className? : string, target : () => Element | null, container : () => Element | null }, popup : HTMLDivElement) => {
                S(() => {
                    // Set default className early so that when we detect bounding client rects below, any
                    // width/height assigned via CSS targeting the given className is already reflected, even
                    // if target and container are both already available on the initial run. But be careful
                    // not to clobber any "above" or "below" className we might've applied on an earlier run
                    // of this computation, as that can be a performance killer, at least on Chrome.
                    const defaultClassName = cx('schedule-popup-container', className);
                    if (popup.className.indexOf(defaultClassName) !== 0) popup.className = defaultClassName;

                    const
                        target = _target(),
                        container = _container();

                    if (!target || !container) return;

                    const
                        tgt = target.getBoundingClientRect(),
                        pop = popup.getBoundingClientRect(),
                        cont = container.getBoundingClientRect();

                    const
                        // How much of the popup would currently be offscreen if we placed it below
                        offscreenIfBelow = Math.max(tgt.bottom + pop.height - window.innerHeight, 0),
                        // How much of the popup would currently be offscreen if we placed it above
                        offscreenIfAbove = Math.max(pop.height - tgt.top, 0),
                        // How much of the popup would overflow the top of the document if we placed it above
                        offDocumentIfAbove = Math.max(pop.height - tgt.top - document.documentElement.scrollTop, 0),
                        // Go above if that lets more of the popup be on screen than going below would. But go
                        // below if going above would result in the popup overflowing the top of the document,
                        // since users can always scroll down to view the whole popup if it hangs off the bottom
                        // of the document, while the popup just becomes inaccessible if it hangs off the top of
                        // the document.
                        above = offscreenIfAbove < offscreenIfBelow && offDocumentIfAbove === 0,
                        mid = (Math.min(cont.right, tgt.right) + Math.max(cont.left, tgt.left)) / 2,
                        left = Math.min(Math.max(mid - pop.width / 2 - cont.left, -cont.left), cont.width - pop.width), // Prioritizing popup's right side for when the popup is larger than the screen
                        top = (above ? Math.max(tgt.top, 0) - pop.height : Math.min(tgt.bottom, cont.bottom)) - cont.top,
                        caretLeft = mid - cont.left - left;

                    popup.style.left = left + 'px';
                    popup.style.top = top + 'px';
                    popup.className = cx(defaultClassName, above ? 'above' : 'below');

                    const caret = popup.childNodes[0] as HTMLDivElement;
                    caret.style.left = caretLeft + "px";
                });
            });
        }

        srender({ children } : { className? : string, target : () => Element | null, container : () => Element | null, children : JSX.Element }) {
            // DON'T set className here, as it can clobber the className set via our SuperCriticalReference above.
            return <div ref={this.ref}>
                <div className="popup-caret"></div>
                <div className="popup-content">
                    {children}
                </div>
            </div>;
        }
    },
    BollardDescriptionTooltip = SuperCritical(({ pg, pgEl, bollardEls } : {
        pg : PierGroupModel,
        pgEl : () => Element | null,
        bollardEls: ElMap<BollardPosition>
    }) => {
        const
            bol = pg.SelectedBollard(),
            descr = bol?.Description();
        return descr ?
            <Popup
                className="bollard-description-tooltip"
                target={elMapSlot(bollardEls, bol)}
                container={pgEl}
            >
                {descr}
            </Popup> :
            null
    }),
    UnlistedReservationDetailsPopup = SuperCritical(({ tool : { UnlistedReservation: { UnlistedReservation: ur } }, target, container } : {
        tool : UnlistedReservationDetailsTool,
        target : () => Element | null,
        container : () => Element | null
    }) => {
        const schedAuth = ur.Berth() && ur.Berth()!.SchedulingAuthorityId();

        return (
            <Popup className="timeline-tool-popup" target={target} container={container}>
                <div className={cx("unlisted-reservation-details", {
                    // DC 09/14/2017 -- Suppress Unlisted / Unavailable information when no Scheduling Authority present
                    'unlisted-reservation-and-hidden' : !schedAuth })}>
                    <div className="header">
                        <span className="timeline-tool-popup-label">Berth Unavailable</span>
                    </div>
                    <div className="content">
                        <span className="berth-label">{ur.Berth() && ur.Berth()!.Location() && ur.Berth()!.Location()!.Name()}</span>

                        <div className="line-time">
                            <span className="line-label">Start</span>
                            <span className="line-time">{tzmoment(ur.Start()).format('MM/DD/YYYY [at] HH:mm')}</span>
                        </div>

                        <div className="line-time">
                            <span className="line-label">End</span>
                            <span className="line-time">{tzmoment(ur.End()).format('MM/DD/YYYY [at] HH:mm')}</span>
                        </div>
                    </div>
                </div>
            </Popup>
        );
    }),
    BerthClosureDetailsPopup = SuperCritical(({ tool, vm, target, container } : {
        tool : BerthClosureDetailsTool,
        vm : ScheduleGrid,
        target : () => Element | null,
        container : () => Element | null
    }) => {
        return (
            <Popup
                className={cx('timeline-tool-popup', {
                    'attention-grabber': tool.AttentionGrabber.Active()
                })}
                target={target}
                container={container}
            >
                <div className="berth-closure-details">
                    <BerthClosureDetailsHeader tool={tool} />
                    <div className="content">
                        <BerthClosureEditForm closure={tool.BerthClosure} vm={vm} />
                        <BerthClosureDetailsSaveControls tool={tool} />
                    </div>
                </div>
            </Popup>
        );
    }),
    BerthClosureDetailsHeader = SuperCritical(({ tool } : { tool : BerthClosureDetailsTool }) =>
        <div className="header">
            {tool.ShowConfirmDelete() ?
                <BerthClosureDetailsDeleteConfirmation tool={tool} /> :
                // Show actions menu toggle provided there's at least one action available.
                // Keep in sync w/ BerthClosureDetailsActionsMenu below.
                tool.BerthClosure.Edit() || (tool.BerthClosure.Overlaps().length > 0 && !tool.BerthClosure.HasUnsavedChanges()) ?
                    <a
                        href=""
                        className="berth-closure-actions-menu-btn fa fa-bars"
                        onClick={noDefault(() => tool.ToggleActionsMenu())}
                    /> :
                    null
            }
            <BerthClosureDetailsActionsMenu tool={tool} />
        </div>
    ),
    BerthClosureDetailsDeleteConfirmation = SuperCritical(({ tool } : { tool : BerthClosureDetailsTool }) =>
        <div className="delete-closure-confirmation">
            <span>Are you sure?</span>
            {' '}
            <a className="delete-closure-confirmation-yes" href="" onClick={noDefault(() => tool.DeleteClosure())}>Yes</a>
            {' '}
            <a className="delete-closure-confirmation-no"  href="" onClick={noDefault(() => tool.ShowConfirmDelete(false))}>No</a>
        </div>
    ),
    BerthClosureDetailsActionsMenu = SuperCritical(({ tool } : { tool : BerthClosureDetailsTool }) =>
        tool.ShowActionsMenu() ?
            <PCDismissible className="pc-actions-menu pc-card" onDismiss={() => tool.ShowActionsMenu(false)}>
                {/* NOTE: Keep any change to visibility of these items in sync w/ BerthClosureDetailsHeader logic above */}
                {tool.BerthClosure.Edit() &&
                    <div className="action-item">
                        <a href="" onClick={noDefault(tool.MenuAction(() => tool.ShowConfirmDelete(true)))}>
                            Delete Closure
                        </a>
                    </div>
                }
                {tool.BerthClosure.Overlaps().length > 0 && !tool.BerthClosure.HasUnsavedChanges() &&
                    <div className="action-item">
                        <a href="" onClick={noDefault(tool.MenuAction(() => tool.ViewOverlaps()))}>
                            View Conflicts
                        </a>
                    </div>
                }
            </PCDismissible> :
            null
    ),
    BerthClosureDetailsSaveControls = SuperCritical(({ tool } : { tool : BerthClosureDetailsTool }) =>
        tool.BerthClosure.Edit() ?
            <div className="save-controls">
                <button
                    className="pc-button pc-button-primary"
                    onClick={() => tool.BerthClosure.CommitChanges()}
                    disabled={!tool.BerthClosure.HasUnsavedChanges() || !tool.BerthClosure.ValidForSubmit()}
                >
                    Update
                </button>
                {tool.BerthClosure.HasUnsavedChanges() &&
                    <a href="" onClick={noDefault(() => tool.BerthClosure.RevertChanges())}>Quit changes</a>
                }
            </div> :
            null
    ),
    ReservationDetailsPopup = SuperCritical(({ tool, target, container } : { tool : ReservationDetailsTool, target : () => Element | null, container : () => Element | null }) => {
        const
            res = tool.Reservation.Reservation,
            pt = tool.Reservation.ItineraryPoint,
            pc = pt.PortCall(),
            hasActuals = pt.Arrival.Actual() !== null || pt.Departure.Actual() !== null,
            hasEstimates = pt.Arrival.Actual() === null || pt.Departure.Actual() === null,
            hasOverlaps = tool.Reservation.Overlaps().length > 0;
        return (
            <Popup
                className={cx('timeline-tool-popup', {
                    invalid: res.Status() === BerthReservationStatus.Conflicted
                })}
                target={target}
                container={container}
            >
                <div className="reservation-details" >
                    <div className="header">
                        <span className="timeline-tool-popup-label">{pc && pc.DisplayVesselIdentifier()}</span>
                        <a className="action" href = {'#portcallprofile?id=' + pt.PortCallId()}>View PortCall</a>
                    </div>
                    <div className="content">
                        <IssuesShortSummary issues={res.Issues} />
                        {hasOverlaps ?
                            <div className="view-overlaps" onClick={noDefault(e => tool.ViewOverlaps())}>
                                <a>View Overlap Conflicts</a>
                            </div>
                        : null}
                        {pc === null ? null : pc.ActiveReservations().map(ores =>
                            ores.Id() === res.Id() ?
                                <div key={ores.Id()} className="this-shift">
                                    <span className="berth-label">{pt.Location() && pt.Location()!.Name()}</span>
            
                                    <div className="line-time">
                                        <span className="line-label">First Line</span>
                                        <span className="line-time">{tzmoment(pt.Arrival.Effective()).format('MM/DD/YYYY [at] HH:mm')}</span>
                                        {pt.Arrival.Actual() ? <span className="log-actual">Actual</span> : null}
                                        {pt.Arrival.Actual() === null && tool.Reservation.EditActuals()
                                            ? <a className="log-actual" onClick = {noDefault(() => tool.LogActual(true))}>Log&nbsp;Actual</a> 
                                            : null}
                                    </div>
            
                                    <div className="line-time">
                                        <span className="line-label">Last Line</span>
                                        <span className="line-time">{tzmoment(pt.Departure.Effective()).format('MM/DD/YYYY [at] HH:mm')}</span>
                                        {pt.Departure.Actual() ? <span className="log-actual">Actual</span> : null}
                                        {pt.Departure.Actual() === null && tool.Reservation.EditActuals()
                                            ? <a className="log-actual" onClick = {noDefault(() => tool.LogActual(false))}>Log&nbsp;Actual</a>
                                            : null}
                                    </div>
                                </div>
                            : <div key={ores.Id()} className="other-shift" onClick={noDefault(() => tool.ShiftReservation(ores))}>
                                    <a>View Shift - {ores.Itinerary() && ores.Itinerary()!.Location() && ores.Itinerary()!.Location()!.Name()} ({ores.Itinerary() && tzmoment(ores.Itinerary()!.Arrival.Effective()).format('M/DD')})</a>
                                </div>)
                        }
                    </div>
                    <div className="footer">
                        {tool.Reservation.Edit() && !hasActuals ? <a className="left-link" onClick = {noDefault(() => tool.ChangeBerth())}>Change Berth</a> : null}
                        {tool.Reservation.Edit() && hasEstimates || tool.Reservation.EditActuals() 
                            ? <a className="center-link" onClick={noDefault(() => tool.EditTimeblock())}>Edit Timeblock</a> 
                            : null}
                        {tool.Reservation.Edit() ? <a className="right-link"  onClick={noDefault(() => tool.AddShift())}>Add Shift</a> : null}
                    </div>
                </div>
            </Popup>
        );
    }),
    NewReservationPopup = SuperCritical(({tool, model: m, target, container } : { tool : NewReservationTool, model : ScheduleGrid, target : () => Element | null, container : () => Element | null }) =>
        <Popup className="timeline-tool-popup" target={target} container={container}> 
            <div className="new-reservation">
                <div className="header">
                    <span className="timeline-tool-popup-label">New PortCall</span>
                    <a className="action" href="#" onClick={noDefault(() => tool.Reserve())}>Reserve Berth</a>
                </div>
                <div className="content">
                    <div className="input-group-title">Line Times</div>
                    <div className="input-group">
                        <span className="input-group-addon">First Line</span>
                        <DateTimePicker value={tool.Arrival} localization={m.Localization} />
                    </div>
                    <div className="input-group">
                        <span className="input-group-addon">Last Line</span>
                        <DateTimePicker value={tool.Departure} localization={m.Localization} />
                    </div>
                </div>
            </div>
        </Popup>),
    EditTimeBlockPopup = SuperCritical(({ tool, model: m, target, container } : { tool : EditTimeblockTool, model : ScheduleGrid, target : () => Element | null, container : () => Element | null }) => {
        const
            res = tool.Reservation.Reservation,
            pt = tool.Reservation.ItineraryPoint,
            pc = pt.PortCall();
        return (
            <Popup
                className={cx('timeline-tool-popup editing', {
                    invalid: res.Status() === BerthReservationStatus.Conflicted,
                    'attention-grabber': tool.AttentionGrabber.Active()
                })}
                target={target}
                container={container}
            >
                <div className="edit-timeblock">
                    <div className="header">
                        <span className="timeline-tool-popup-label">
                            Edit - {pc && pc.DisplayVesselIdentifier()}
                        </span>
                    </div>
                    <div className="content no-top-border">
                        <div className="input-group-title">Line Times</div>
                        <div className="input-group first-line">
                            <span className="input-group-addon">First Line</span>
                            <DateTimePicker
                                value={pt.Arrival.Effective}
                                disabled={!!(pt.Arrival.Actual() && !tool.Reservation.EditActuals())}
                                localization={m.Localization}
                            />
                            {pt.Arrival.Actual() ? <span className="log-actual">Actual</span> : null}
                            {pt.Arrival.Actual() && tool.Reservation.EditActuals() ? <a className="log-actual" onClick={noDefault(() => pt.Arrival.Actual(null))}>Remove</a> : null}
                            {pt.Arrival.Actual() === null && tool.Reservation.EditActuals() ? <a className="log-actual" onClick={noDefault(() => tool.LogActual(true))}>Log&nbsp;Actual</a> : null}
                        </div>
                        <div className="input-group last-line">
                            <span className="input-group-addon">Last Line</span>
                            <DateTimePicker
                                value={pt.Departure.Effective}
                                disabled={!!(pt.Departure.Actual() && !tool.Reservation.EditActuals())}
                                localization={m.Localization}
                            />
                            {pt.Departure.Actual() ? <span className="log-actual">Actual</span> : null}
                            {tool.Reservation.EditActuals()
                                ? pt.Departure.Actual() 
                                    ? <a className="log-actual" onClick={noDefault(() => pt.Departure.Actual(null))}>Remove</a> 
                                    : <a className="log-actual" onClick={noDefault(() => tool.LogActual(false))}>Log&nbsp;Actual</a> 
                                : null
                            }
                        </div>
                    </div>
                    <div className="footer">
                        <div className="mode-footer">
                            <a onClick={noDefault(() => tool.Confirm())}>Confirm</a>
                            {" "}&bull;{" "}
                            <a onClick={noDefault(() => tool.Cancel())}>Quit Changes</a>
                        </div>
                    </div>
                </div>
            </Popup>
        );
    }),
    ChangeBerthPopup = SuperCritical(({ tool, model: m, target, container } : { tool : ChangeBerthTool, model : ScheduleGrid, target : () => Element | null, container : () => Element | null }) => {
        const
            pt = tool.Reservation.ItineraryPoint,
            pc = pt.PortCall();
        return (
            <Popup
                className={cx('timeline-tool-popup editing', {
                    'attention-grabber': tool.AttentionGrabber.Active()
                })}
                target={target}
                container={container}
            >
                <div className="change-berth">
                    <div className="header">
                        <span className="timeline-tool-popup-label">
                            Change Berth - {pc && pc.DisplayVesselIdentifier()}
                        </span>
                    </div>
                    <div className="content no-top-border">
                        <div className="input-group-title">Line Times</div>
                        <div className="input-group">
                            <span className="input-group-addon">First Line</span>
                            <DateTimePicker value={pt.Arrival.Effective} localization={m.Localization} />
                        </div>
                        <div className="input-group">
                            <span className="input-group-addon">Last Line</span>
                            <DateTimePicker value={pt.Departure.Effective} localization={m.Localization} />
                        </div>
                    </div>
                    <div className="footer">
                        <div className="mode-footer">
                            <a onClick={noDefault(() => tool.Confirm())}>Confirm</a>
                            {" "}&bull;{" "}
                            <a onClick={noDefault(() => tool.Cancel())}>Quit Changes</a>
                        </div>
                    </div>
                </div>
            </Popup>
        );
    }),
    ConfirmAddShiftPopup = SuperCritical(({ tool, model: m, target, container } : { tool : AddShiftTool, model : ScheduleGrid, target : () => Element | null, container : () => Element | null }) =>
        <Popup
            className={cx('timeline-tool-popup editing', {
                'attention-grabber': tool.AttentionGrabber.Active()
            })}
            target={target}
            container={container}
        >
            <div className="confirm-add-shift">
                <div className="header">
                    <span className="timeline-tool-popup-label">
                        Add Shift - {tool.PortCall.DisplayVesselIdentifier()}
                    </span>
                </div>
                <div className="content no-top-border">
                    <div className="input-group-title">Line Times</div>
                    <div className="input-group">
                        <span className="input-group-addon">First Line</span>
                        <DateTimePicker value={tool.Arrival} localization={m.Localization} />
                    </div>
                    <div className="input-group">
                        <span className="input-group-addon">Last Line</span>
                        <DateTimePicker value={tool.Departure} localization={m.Localization} />
                    </div>
                </div>
                <div className="footer">
                    <div className="mode-footer">
                        <a onClick={noDefault(() => tool.Confirm())}>Confirm</a>
                        {" "}&bull;{" "}
                        <a onClick={noDefault(e => tool.Dragging(true))}>Keep Looking</a>
                        {" "}&bull;{" "}
                        <a onClick={noDefault(() => tool.Cancel())}>Quit Changes</a>
                    </div>
                </div>
            </div>
        </Popup>
    ),
    ReservationOverlapsPopup = SuperCritical(({ tool, target, container } : { tool : ReservationOverlapsTool, target : () => Element | null, container : () => Element | null }) => {
        const
            res = tool.Reservation.Reservation,
            // Include this res in overlap list
            thisOverlap : OverlapsPopupOverlap = { key: 0, issue: null, resId: res.Id(), res, bclId: 0, bcl: null },
            overlaps = lift(() => [thisOverlap]).concat(tool.Reservation.Overlaps.map(i => ({
                key: i.Id(),
                issue: { i, dir: IssueDirection.OwnIssue },
                resId: i.OtherReservationId(),
                res: i.OtherReservation(),
                bclId: i.BerthReservationBerthClosureId(),
                bcl: i.BerthReservationBerthClosure()
            })));
        return <OverlapsPopup overlaps={overlaps} tool={tool} target={target} container={container} />;
    }),
    BerthClosureOverlapsPopup = SuperCritical(({ tool, target, container } : { tool : BerthClosureOverlapsTool, target : () => Element | null, container : () => Element | null }) => {
        const
            bcl = tool.BerthClosure.BerthClosure,
            // Include this closure in overlap list
            thisOverlap : OverlapsPopupOverlap = { key: 0, issue: null, resId: 0, res: null, bclId: bcl.Id(), bcl },
            overlaps = lift(() => [thisOverlap]).concat(tool.BerthClosure.Overlaps.map(i => ({
                key: i.Id(),
                issue: { i, dir: i.BerthClosureId() ? IssueDirection.OwnIssue : IssueDirection.OthersIssue },
                resId: i.BerthReservationId(),
                res: i.BerthReservation(),
                bclId: i.OtherClosureId(),
                bcl: i.OtherClosure()
            })));
            return <OverlapsPopup overlaps={overlaps} tool={tool} target={target} container={container} />;
        }),
    OverlapsPopup = SuperCritical(({ tool, overlaps: _overlaps, target, container } : {
        tool : OverlapsPopupTool,
        overlaps : () => OverlapsPopupOverlap[],
        target : () => Element | null,
        container : () => Element | null
    }) => {
        const overlaps = _overlaps();

        // Sort overlaps by conflict priority.
        // For reservations, there's an easy transitive ordering based on ApprovedDate and RequestDate that
        // agrees w/ our server-side issue severity logic.
        // For berth closures things are messier: Our server-side issue severity logic depends on the overlap
        // issue's DetectDate vs the reservation's ApprovedDate, and it turns out there's no transitive ordering
        // of closures and reservations that agrees w/ that logic in all cases (let alone agrees w/ that logic AND
        // w/ our logic for reservation-to-reservation overlaps). An example: Imagine an initial state where there
        // are two reservations, R1 and R2, neither of which has been approved, and two closures C1 and C2 such
        // that C1 overlaps R1 but not R2 and C2 overlaps R2 but not R1. (It doesn't matter in this case whether
        // R1 and R2 overlap each other or whether C1 and C2 overlap each other). Next, R1 is approved, giving it
        // priority over C1 by our server-side rules. Next, R2 is approved, giving it priority over C2 by our
        // server-side rules and also priority over R1 should they overlap. Next, C1 is extended such that it now
        // overlaps R2 in addition to still overlapping R1. Finally, C2 is extended such that it now overlaps R1
        // in addition to still overlapping R2. By our server-side rules, R2 now has a conflict due to C1, and R1
        // now has a conflict due to C2, even though R1's overlap w/ C1 and R2's overlap w/ C2 both have severity
        // Information. I.e. in terms of the relationship > meaning "has conflict priority over," we simultaneously
        // have R1 > C1, C1 > R2, R2 > C2, and C2 > R1 (and R2 > R1 based on approved date). We can't represent
        // that in a single linear ordering, so we'll say that in general closures have priority over reservations,
        // w/ an extra twist to make sure we sort whichever item is the subject of this popup in the correct order.
        // Then, finally, there are unlisted reservations. These show up w/ neither res nor bcl populated.
        // We'll arbitrarily sort them btwn approved reservations and non-approved ones, on the assumption
        // that it isn't that important for agents and other non-berth-mgrs to understand exactly where
        // unlisted reservations fit in the priority order.
        overlaps.sort((a, b) =>
            // Special handling for the subject of the overlaps popup: If one of the items being compared
            // is the subject of this overlaps popup (as indicated by its i property being null), then sort
            // based on the severity of the issue.
            a.issue                               && !b.issue                              ? a.issue.dir * (a.issue.i.Severity() >= IssueSeverity.Conflict ? -1 : 1) :
            !a.issue                              && b.issue                               ? b.issue.dir * (b.issue.i.Severity() >= IssueSeverity.Conflict ? 1 : -1) :

            // Reservation-to-reservation -- Sort based on ApprovedDate and RequestDate
            a.res                             && b.res                             ?
            a.res.ApprovedDate()              && !b.res.ApprovedDate()             ? -1 :
            !a.res.ApprovedDate()             && b.res.ApprovedDate()              ? 1 :
            a.res.ApprovedDate()              && b.res.ApprovedDate()              ? +b.res.ApprovedDate()! - +a.res.ApprovedDate()! :
            +a.res.RequestDate() - +b.res.RequestDate()                            :

            // Reservation-to-closure -- Just say the closure wins, even though the server-side rules are
            // actually more nuanced than this.
            a.res                             && b.bcl                             ? 1 :
            a.bcl                             && b.res                             ? -1 :

            // Reservation-to-unlisted reservation -- Sort based on ApprovedDate
            a.res                             && !b.res                            ? a.res.ApprovedDate() ? -1 : 1 :
            !a.res                            && b.res                             ? b.res.ApprovedDate() ? 1 : -1 :

            // Closure-to-closure -- Just call it a tie.
            a.bcl                             && b.bcl                             ? 0 :

            // Closure-to-unlisted reservation -- Just say the closure wins.
            a.bcl                             && !b.res                            ? -1 :
            !a.res                            && b.bcl                             ? 1 :

            // Unlisted reservation-to-unlisted reservation -- Just call it a tie.
            0
        );

        return (
            <Popup className="timeline-tool-popup invalid" target={target} container={container}>
                <div className="reservation-overlaps" >
                    <div className="header">
                        <span className="timeline-tool-popup-label">Overlap Conflicts ({overlaps.length})</span>
                    </div>
                    <div className="content">
                        <table>
                            <thead>
                                <tr>
                                    <th className="vessel-name">Reservation Order</th>
                                    <th className="request-time">Requested</th>
                                    <th className="approved-time">Booked</th>
                                </tr>
                            </thead>
                            <tbody>
                                {overlaps.map(o => {
                                    const
                                        onClick =
                                            o.resId ? () => tool.ViewConflictingReservation(o.resId) :
                                            o.bclId ? () => tool.ViewConflictingBerthClosure(o.bclId) :
                                            undefined,
                                        vesselName = o.res ? o.res.PortCall() && o.res.PortCall()!.DisplayVesselIdentifier() :
                                            // \u2014 == emdash
                                            o.bcl ? `Closure \u2014 ${o.bcl.Title()}` :
                                            'Berth Unavailable',
                                        requestDate = o.res && o.res.RequestDate(),
                                        approvedDate = o.res && o.res.ApprovedDate();
                                    return (
                                        <tr key={o.key} className="overlap" onClick={onClick && noDefault(onClick)}>
                                            <td className="vessel-name">{vesselName}</td>
                                            <td className="request-time">{requestDate && priorityDateFormat(requestDate, tool.Now())}</td>
                                            <td className="approved-time">{approvedDate && priorityDateFormat(approvedDate, tool.Now())}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="footer">
                    </div>
                </div>
            </Popup>
        );

        function priorityDateFormat(date : TZDate, now : TZDate) : string {
            const diff = +now - +date,
                mins = Math.floor(diff / 60 / 1000),
                hours = Math.floor(diff / 60 / 60 / 1000),
                days = Math.floor(diff / 24 / 60 / 60 / 1000);

            return (
                mins === 1  ? '1 minute ago' :
                mins < 240  ? `${mins} minutes ago` : 
                hours < 48  ? `${hours} hours ago` :
                `${days} days ago`
            )
        }
    }),
    AddBerthClosureModal = SuperCritical(({ vm } : { vm : ScheduleGrid }) => {
        const model = vm.AddBerthClosure();
        return (
            <PCModal shown={() => !!model} className="add-berth-closure-modal" onBackDropClick={() => vm?.ShowAddBerthClosure(false)}>
                <div className="modal-content pc-card">
                    <div className="pc-card-header">
                        <a href="" onClick={noDefault(() => vm.ShowAddBerthClosure(false))}>
                            &lt;&lt; Back
                        </a>
                    </div>
                    <div className="modal-body">
                        {model &&
                            <div>
                                <BerthClosureEditForm closure={model} vm={vm} />
                                <div className="add-berth-closure-buttons">
                                    <button
                                        className="submit-btn pc-button pc-button-primary"
                                        onClick={() => model.Submit()}
                                        disabled={!model.ValidForSubmit()}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </PCModal>
        );
    }),
    BerthClosureEditForm = SuperCritical(({ closure: model, vm } : { closure : BerthClosureModel | AddBerthClosureModel, vm : ScheduleGrid }) =>
        <div className="berth-closure-edit-form">
            <div className="berth-closure-header">Berth Closure</div>
            <div className="berth-closure-edit-row">
                <label className="berth-closure-label pc-text-smalllabel">Title</label>
                <BerthClosureTitleInput closure={model} />
                <LightErrorView error={() => model.Validation()['Title']} />
            </div>
            <div className="berth-closure-edit-row">
                <label className="berth-closure-label datetime-label pc-text-smalllabel">Start</label>
                <DateTimePicker
                    value={model.BerthClosure.StartDate}
                    disabled={!(model instanceof AddBerthClosureModel) && !model.Edit()}
                    localization={vm.Localization}
                />
                <LightErrorView error={() => model.Validation()['StartDate']} />
            </div>
            <div className="berth-closure-edit-row">
                <label className="berth-closure-label datetime-label pc-text-smalllabel">End</label>
                <NullableDateTimePicker
                    value={model.BerthClosure.EndDate}
                    disabled={!(model instanceof AddBerthClosureModel) && !model.Edit()}
                    localization={vm.Localization}
                />
                <span className={cx("berth-closure-enddate-right-content", { "invalid-enddate": !!model.Validation()['EndDate'] })}>
                    <LightErrorView error={() => model.Validation()['EndDate']} />
                    <div>
                        {(model instanceof AddBerthClosureModel || model.Edit()) && model.BerthClosure.EndDate() &&
                            <a href="" onClick={noDefault(() => model.BerthClosure.EndDate(null))}>
                                Close Indefinitely
                            </a>
                        }
                    </div>
                </span>
            </div>
            <div className="berth-closure-edit-row">
                <label className="berth-closure-label pc-text-smalllabel">Berth</label>
                <BerthSelectorView
                    className="berth-closure-input"
                    model={model.BerthSelector}
                    disabled={!(model instanceof AddBerthClosureModel) && !model.Edit()}
                />
                <LightErrorView error={() => model.Validation()['BerthId']} />
            </div>
            <div className="berth-closure-edit-row">
                <label className="berth-closure-label pc-text-smalllabel">Footmark Start</label>
                <NumericInput
                    className="berth-closure-numeric-input"
                    value={model.StartmarkNum}
                    disabled={!(model instanceof AddBerthClosureModel) && !model.Edit()}
                />
                <span className="berth-closure-uom">{model.BerthClosure.Footmark.UOM()}</span>
                <LightErrorView error={() => model.Validation()['Footmark']} />
            </div>
            <div className="berth-closure-edit-row">
                <label className="berth-closure-label pc-text-smalllabel">Footmark End</label>
                <NumericInput
                    className="berth-closure-numeric-input"
                    value={model.EndmarkNum}
                    disabled={!(model instanceof AddBerthClosureModel) && !model.Edit()}
                />
                <span className="berth-closure-uom">{model.BerthClosure.Footmark.UOM()}</span>
                <LightErrorView error={() => model.Validation()['Length']} />
            </div>
            {/* something that gets at that we're temporarily treating remarks as visible for berth managers+ 
            only b/c Oahu berth managers want somewhere to record who created the closure when 

            "Longer term, we might either add history-tracking features for berth closures or split Remarks into two fields: Remarks and AdminRemarks."
            */}
            {(model instanceof AddBerthClosureModel || model.Edit()) &&
                 <div className="berth-closure-edit-row">
                     <label className="berth-closure-label pc-text-smalllabel">Remarks</label>
                     <textarea
                       className="berth-closure-multiline-input"
                       rows={4}
                       value={model.BerthClosure.Remarks() || ''}
                       onChange={e => model.BerthClosure.Remarks(e.currentTarget.value)}
                       disabled={!(model instanceof AddBerthClosureModel) && !model.Edit()}
                     />
                 </div>
            }
        </div>
    ),
    BerthClosureTitleInput = class BerthClosureTitleInput extends SuperCriticalComponent<{ closure : BerthClosureModel | AddBerthClosureModel }> {
        // Long enough for the animation on modal opening to complete
        static readonly FOCUS_DELAY_MS = 200;

        inputRef : (el : HTMLInputElement | null) => void;

        constructor(props : { closure : BerthClosureModel | AddBerthClosureModel }) {
            super(props);

            // Focus the title input when opening the Add Closure modal. Can't do this via a
            // simple autoFocus property b/c of timing: Need to focus the input AFTER the modal
            // animation is complete.
            this.inputRef = SuperCriticalReference(this, ({ closure }, el) => {
                if (closure instanceof AddBerthClosureModel) {
                    const timeoutId = setTimeout(() => el.focus(), BerthClosureTitleInput.FOCUS_DELAY_MS);
                    S.cleanup(() => clearTimeout(timeoutId));
                }
            });
        }

        srender({ closure: model } : { closure : BerthClosureModel | AddBerthClosureModel }) {
            return (
                <input
                    type="text"
                    className="berth-closure-input"
                    ref={this.inputRef}
                    value={model.BerthClosure.Title()}
                    onChange={e => model.BerthClosure.Title(e.currentTarget.value)}
                    disabled={!(model instanceof AddBerthClosureModel) && !model.Edit()}
                />
            );
        }
    },
    translateScale = (
        tzero : () => TZDate, 
        trange : () => number, 
        xrange : () => number, 
        drange : () => Measurement,
        yrange : () => number,
        tzutils : TZUtils
    ) : Scale => {
        let x = (a : TZDate, b? : TZDate) => Math.round(((b || a).getTime() - (b ? a : tzero()).getTime()) / trange() * xrange()),
            y = (m : Measurement) => m.convert(drange().UOM()).Num() / drange().Num() * yrange(),
            date = (x : number) => tzutils.newDate(tzero().getTime() + x / xrange() * trange()),
            pier = (y : number) => new Measurement({ Num: y / yrange() * drange().Num(), UOM: drange().UOM() });

        return { x, y, date, pier };
    },
    // translate a mouse event into the appropriate model properties: a piergroup, a piermark and a date
    translateEvent = (pgs : ElMap<PierGroup>, scale : Scale) : TranslateEvent => (click : React.MouseEvent<SVGElement> | React.MouseEvent<HTMLElement> | MouseEvent) : GridLocation | null => {
        let pg : PierGroup | null = null,
            x = 0,
            y = 0;

        for (var kvp of Array.from(pgs)) {
            let el = kvp[1]();
            if (el) {
                let rect = el.getBoundingClientRect();
                if (click.clientY > rect.top && click.clientY < rect.bottom) {
                    pg = kvp[0];
                    x = click.clientX - rect.left;
                    y = click.clientY - rect.top;
                    break;
                }
            }
        }

        return pg === null ? null : {
            PierGroup : pg,
            PierMark : scale.pier(y).convert(pg.Length.UOM()),
            Date : scale.date(x),
            X : click.pageX,
            Y : click.pageY
        };
    },
    // given a model drag function, which takes a GridLocation, and a function to translate mouse events to GridLocations,
    // initiate a drag
    translateDrag = (txevent : TranslateEvent, drag : (loc : GridLocation) => void) => {
        let mousemove = (e : MouseEvent) => {
                var loc = txevent(e);
                if (loc) drag(loc);
                e.preventDefault();
            },
            mouseup = (e : MouseEvent) => {
                document.removeEventListener("mousemove", mousemove);
                document.removeEventListener("mouseup", mouseup);
            };
            
        document.addEventListener("mousemove", mousemove);
        document.addEventListener("mouseup", mouseup);
    },
    translateTimelinePositionCoords = (compact : () => boolean, scale : Scale, berthHeights : { [ id : number ] : YGeo }, resOffsets : { [key: string] : number }) => (pos : ITimelinePosition) : Rectangle => {
        const
            id = pos.UniqueKey(),
            berth = pos.Berth(),
            length = pos.Length(),
            start = pos.Start(),
            end = pos.End();

        return {
            x: start ? scale.x(start) : 0,
            y: !compact() ? scale.y(pos.PierMark) : (berth ? berthHeights[berth.Id()].y : 0) + (COMPACT_RES_HEIGHT + COMPACT_OVERLAP_GAP) * resOffsets[id] + COMPACT_BERTH_HEIGHT / 2,
            width: start && end ? Math.max(20, scale.x(start, end)) : 0,
            height: compact() ? COMPACT_RES_HEIGHT : length ? Math.max(10, scale.y(length)) : 0
        };
    };

enum IssueDirection {
    // NOTE: The sign of these values is significant. Check references before editing.
    /**
     * The owner of the issue is the subject of the overlaps popup.
     */
    OwnIssue = 1,
    /**
     * The issue is owned by another item and is CAUSED by the subject of the
     * overlaps popup.
     */
    OthersIssue = -1
}

interface OverlapsPopupIssue {
    i : Issue;
    dir : IssueDirection;
}

/** An overlap displayed in an overlaps popup */
interface OverlapsPopupOverlap {
    key : number;
    /**
     * The issue associated w/ this overlap, or null if this object represents
     * the subject of the overlaps popup.
     */
    issue : OverlapsPopupIssue | null;
    resId : number;
    res : BerthReservation | null;
    bclId : number;
    bcl : BerthClosure | null;
}

// An ITimelineItemTool suitable for rendering an overlaps popup.
interface OverlapsPopupTool {
    Now() : TZDate;
    ViewConflictingReservation(resId : number) : void;
    ViewConflictingBerthClosure(bclId : number) : void;
}


// utilities for maps mapping entities to their elements
type ElMap<T> = Map<T, DataSignal<Element | null>>;

function ElMap<T>() { 
    return new Map<T, DataSignal<Element | null>>(); 
}

function setElMap<T>(map : ElMap<T>, n : T, el : Element | null) {
    // B/c we're using arrow functions for a lot of our React refs above, React sees a new function object each
    // time the ref is set, and as a result React conservatively calls the old ref function w/ null immediately
    // before it calls the new ref function. But if we get two calls to this function in the same tick of S's clock,
    // one w/ el set to null and one w/ it populatd, S will throw b/c we're setting the same signal to two different
    // values in the same clock tick. So ignore calls in which el is null. This util is intended for entities that
    // live as long as this page does, so we don't care about leaking destroyed elements.
    if (el) elMapSlot(map, n)(el);
}

function getElMap<T>(map : ElMap<T>, n : T) {
    return elMapSlot(map, n)();
}

function elMapSlot<T>(map : ElMap<T>, n : T) {
    let slot = map.get(n) || S.data(null);
    if (!map.has(n)) map.set(n, slot);
    return slot;
}

// utilities for mapping current tool to its element, in case current tool doesn't tie to a timeline item
interface ToolEl {
    tool : ITimelineItemTool;
    el : Element;
}

function setToolEl(toolEl : DataSignal<ToolEl | null>, tool : ITimelineItemTool, el : Element | null) {
    // Like setElMap above, we gotta handle the possibility that React might trigger back-to-back calls to this
    // function, one w/ el set to null and one w/ it populated. But unlike setElMap, we DO wanna catch the case
    // where a tool element is getting destroyed, b/c we wanna know if there's a current tool element or not.
    // So debounce our writes to the toolEl signal.
    window.clearTimeout(setToolElTimeoutId);
    setToolElTimeoutId = window.setTimeout(() => {
        if (el) toolEl({ tool, el });
        else if (S.sample(() => toolEl()?.tool) === tool) toolEl(null);
    });
}

interface TranslateEvent {
    (e : React.MouseEvent<SVGElement> | React.MouseEvent<HTMLElement> | MouseEvent) : GridLocation | null;
}

interface Scale {
    x(a : TZDate, b? : TZDate) : number;
    y(m : Measurement) : number;
    date(x : number) : TZDate;
    pier(y : number) : Measurement;
}

interface YGeo {
    y : number
    height : number
}

interface Rectangle {
    x : number,
    y : number,
    width : number,
    height : number
}


// WEBPACK FOOTER //
// ./apps/portcall/schedulegrid/view.tsx